import { DialogActions, DialogTitle } from '@aibees/shared/components';
import { useBoolean } from '@aibees/shared/hooks';
import { classnames } from '@aibees/shared/utils';
import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Breakpoint, DialogContent } from '@mui/material';
import { cloneElement, memo, useEffect, useRef } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import * as Styled from './styles';
import type { FormDialogProps as Props } from './types';

export const FormDialog = memo(
  ({
    className,
    onSubmit,
    scheme,
    defaultValues,
    submitButtonProps,
    children,
    contentSx,
    actionsSx,
    defaultOpen,
    debug,
    state,
    disabled,
    title,
    icon,
    maxWidth,
    uncontrolledElement,
    onClickUncontrolledElement,
    disableOnInvalid = true,
    resetAfterClose = false,
    customLeftActions,
    ...dialogProps
  }: Props) => {
    const dialogDefaultState = useBoolean(!!defaultOpen);
    const dialog = state || dialogDefaultState;
    const loading = useBoolean();
    const hasInitialized = useRef(false);

    const methods = useForm({
      resolver: scheme && yupResolver(scheme),
      defaultValues: defaultValues || undefined,
      mode: 'onChange',
      reValidateMode: 'onChange',
    });

    // this hook solves the async default values issue, when the default values are loaded after the dialog is opened
    useEffect(() => {
      if (!defaultValues) return;
      const hasDefaultValues = Object.keys(defaultValues || {})
        .map((x) => defaultValues[x])
        .some((x) => x);
      if (hasDefaultValues && !hasInitialized.current) {
        methods.reset(defaultValues);
        hasInitialized.current = true;
      }
    }, [defaultValues]); // Only depend on defaultValues, not methods

    const handleClose = () => {
      dialog.onFalse();
      loading.onFalse();
      hasInitialized.current = false;
      dialogProps.onClose?.();
      if (resetAfterClose) methods.reset();
    };

    const handleSubmit = methods.handleSubmit((data) => {
      onSubmit?.(data, {
        startProgress: loading.onTrue,
        endProgress: loading.onFalse,
        toggle: handleClose,
      });
      if (resetAfterClose) methods.reset();
    });

    const handleUncontrolledElementClick = () => {
      dialog.onToggle();
      onClickUncontrolledElement?.();
    };

    if (debug)
      console.log('[FormDialog]', ((globalThis as any).formState = methods));

    return (
      <>
        {uncontrolledElement &&
          cloneElement(uncontrolledElement, {
            ...uncontrolledElement.props,
            onClick: handleUncontrolledElementClick,
          })}

        <Styled.Wrapper
          className={classnames('FormDialog', className)}
          open={dialog.value}
          onClose={handleClose}
          maxWidth={(maxWidth || 'sm') as Breakpoint}
          {...dialogProps}
        >
          {title && (
            <DialogTitle
              title={title}
              icon={icon === false ? undefined : icon}
              onClose={handleClose}
            />
          )}

          <DialogContent sx={{ p: 2, ...contentSx }}>
            <FormProvider {...methods}>
              <form onSubmit={handleSubmit} id="FormDialogForm">
                {children}
              </form>
            </FormProvider>
          </DialogContent>

          <Box sx={actionsSx}>
            <DialogActions
              customLeftActions={customLeftActions}
              submitTitle={
                typeof submitButtonProps?.children === 'string'
                  ? submitButtonProps.children
                  : 'Save'
              }
              formId="FormDialogForm"
              disabled={
                disabled || (disableOnInvalid && !methods.formState.isValid)
              }
              loading={loading.value || methods.formState.isSubmitting}
              onClose={handleClose}
              submitButtonProps={submitButtonProps}
            />
          </Box>
        </Styled.Wrapper>
      </>
    );
  }
);

FormDialog.displayName = 'FormDialog';
